import {
  enableProdMode,
  isDevMode,
  importProvidersFrom,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { environment } from './environments/environment';
import { provideRouter, RouterModule } from '@angular/router';
import { routes } from './app/routes';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  HttpClient,
} from '@angular/common/http';
import { HeadersInterceptor } from './app/core/interceptors/headers-common.interceptor';
import {
  TranslateService,
  TranslateModule,
  TranslateLoader,
} from '@ngx-translate/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { provideEffects } from '@ngrx/effects';
import {
  authenticateAndPairEffect,
  completePairingSuccessEffect,
  createUserEffect,
  getSessionEffect,
  loginEffect,
  logoutEffect,
  pairProductEffect,
  switchToProductEffect,
  unpairProductEffect,
} from './app/core/state/user-session/user-session.effects';

import {
  resetPasswordEffect,
  submitUsernameEffect,
} from './app/core/state/password-management/password-management.effects';
import {
  authenticateMfaEffect,
  getIsMfaEnabledEffect,
  navigateToOriginEffect,
  navigateToOriginLoginEffect,
} from './app/core/state/mfa/mfa.effects';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideStore } from '@ngrx/store';
import { userSessionReducer } from './app/core/state/user-session/user-session.reducer';
import { passwordManagementReducer } from './app/core/state/password-management/password-management.reducer';
import { mfaReducer } from './app/core/state/mfa/mfa.reducer';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { AppComponent } from './app/app.component';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      RouterModule,
      StoreDevtoolsModule.instrument({
        maxAge: environment.storeDevtoolsModuleMaxAge,
        logOnly: !isDevMode(),
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      MatCardModule,
      MatFormFieldModule,
      MatInputModule,
      MatIconModule,
      MatButtonModule,
      MatDividerModule,
      MatTooltipModule,
      MatCheckboxModule,
      MatSnackBarModule,
      MatMenuModule,
      MatSidenavModule,
      MatListModule,
      NgOptimizedImage,
    ),
    provideStore({
      'user-session': userSessionReducer,
      'password-management': passwordManagementReducer,
      mfa: mfaReducer,
    }),
    provideEffects({
      loginEffect,
      logoutEffect,
      getSessionEffect,
      createUserEffect,
      authenticateAndPairEffect,
      completePairingSuccessEffect,
      pairProductEffect,
      unpairProductEffect,
      switchToProductEffect,
      submitUsernameEffect,
      resetPasswordEffect,
      getIsMfaEnabledEffect,
      authenticateMfaEffect,
      navigateToOriginEffect,
      navigateToOriginLoginEffect,
    }),
    provideRouter(routes),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeadersInterceptor,
      multi: true,
    },
    provideAppInitializer(() => {
      const translateService = inject(TranslateService);
      translateService.setDefaultLang('en');

      return translateService.use('en').toPromise();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
}).catch((err) => console.error(err));

function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
